import { useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchAllGroupedInvoices,
  buildGroupedInvoicesQuery,
  buildMonthlyInvoicesQuery,
  getDimensionScalarField,
} from "../lib/graphql";
import { getCountryName } from "../lib/countries";
import { getPermittedAggregationFieldsForMetrics } from "./usePermissions";
import {
  GridRow,
  DimensionType,
  MetricType,
  BreadcrumbItem,
  LiquidationFilter,
  ExceptionFilter,
  TimePeriodType,
  SortConfig,
  YearType,
} from "../types";
import { UserRole } from "../types/roles";
import type { FactInvoice } from "../types/graphql";

interface UseInvoiceDataOptions {
  dimension: DimensionType;
  filters: BreadcrumbItem[];
  liquidationFilter: LiquidationFilter;
  exceptionFilter: ExceptionFilter;
  timePeriod: TimePeriodType;
  selectedMetrics: MetricType[];
  userRole: UserRole;
  selectedCountries: string[];
  selectedSalesPeople: string[];
  sortConfig: SortConfig | null;
  isAuthenticated: boolean;
}

// Month names for display
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Get actual years for CY, PY, PPY
function getYearNumbers(): { CY: number; PY: number; PPY: number } {
  const currentYear = new Date().getFullYear();
  return {
    CY: currentYear,
    PY: currentYear - 1,
    PPY: currentYear - 2,
  };
}

// Helper to get YTD month limit (current month number 1-12)
function getYtdMonth(): number {
  return new Date().getMonth() + 1; // 1-12
}

// Helper to map dimension to invoice field for breadcrumb filters
function getDimensionField(dimension: DimensionType): keyof FactInvoice {
  const mapping: Record<DimensionType, keyof FactInvoice> = {
    month: "Month",
    department: "Department",
    item: "Article",
    category: "Category",
    productionUnit: "MainGroup",
    group: "MainGroup",
    customer: "Customer",
    customerGroup: "CustomerGroup",
    country: "Country",
    vendor: "Supplier",
    brand: "Brand",
    salesPerson: "SalesPerson",
    sector: "Sector",
    sectorToplevel: "SectorToplevel",
  };
  return mapping[dimension];
}

// Format a dimension value for display
function formatDimensionLabel(
  dimension: DimensionType,
  value: string | null,
): string {
  if (value === null) return "Unknown";

  if (dimension === "month") {
    // For calendar-based queries, value might already be formatted
    return value;
  }

  if (dimension === "country") {
    return getCountryName(value);
  }

  return value;
}

// Get dimension key for grouping
function getDimensionKey(
  _dimension: DimensionType,
  value: string | null,
): string {
  if (value === null) return "unknown";
  return value;
}

export function useInvoiceData({
  dimension,
  filters,
  liquidationFilter,
  exceptionFilter,
  timePeriod,
  selectedMetrics,
  userRole,
  selectedCountries,
  selectedSalesPeople,
  sortConfig,
  isAuthenticated,
}: UseInvoiceDataOptions) {
  const queryAggregationFields = useMemo(
    () => getPermittedAggregationFieldsForMetrics(userRole, selectedMetrics),
    [userRole, selectedMetrics],
  );

  // Build base filter (without date range - that's added per year query)
  const buildBaseFilter = useCallback(() => {
    const filter: Record<string, unknown> = {};

    // Apply liquidation filter
    if (liquidationFilter === "none") {
      filter.Liquidation = { eq: false };
    } else if (liquidationFilter === "only") {
      filter.Liquidation = { eq: true };
    }

    if (exceptionFilter === "none") {
      filter.Exception = { eq: false };
    } else if (exceptionFilter === "only") {
      filter.Exception = { eq: true };
    }

    // Apply breadcrumb filters
    // Month filters are handled separately via getMonthFilter()
    filters.forEach((f) => {
      if (f.dimension !== "month") {
        const field = getDimensionField(f.dimension);
        filter[field] = { eq: f.value };
      }
    });

    // Apply country filter
    if (selectedCountries.length > 0) {
      filter.Country = { in: selectedCountries };
    }

    // Apply sales person filter
    if (selectedSalesPeople.length > 0) {
      filter.SalesPerson = { in: selectedSalesPeople };
    }

    return filter;
  }, [
    liquidationFilter,
    exceptionFilter,
    filters,
    selectedCountries,
    selectedSalesPeople,
  ]);

  // Extract month filter from breadcrumb filters (if any)
  const getMonthFilter = useCallback((): number | null => {
    const monthFilter = filters.find((f) => f.dimension === "month");
    if (monthFilter) {
      const monthNum = parseInt(monthFilter.value, 10);
      if (!isNaN(monthNum) && monthNum >= 1 && monthNum <= 12) {
        return monthNum;
      }
    }
    return null;
  }, [filters]);

  // Build year filter (optionally constrained to a month or YTD)
  const buildYearFilter = useCallback(
    (
      year: number,
      monthFilter: number | null,
      ytdMaxMonth: number | null, // For YTD filtering on non-month dimensions
    ): Record<string, unknown> => {
      const filter: Record<string, unknown> = {
        Year: { eq: year },
      };

      if (monthFilter !== null) {
        // Specific month filter from breadcrumbs
        filter.Month = { eq: monthFilter };
      } else if (ytdMaxMonth !== null) {
        // YTD filter: months 1 through ytdMaxMonth
        filter.Month = { lte: ytdMaxMonth };
      }

      return filter;
    },
    [],
  );

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: [
      "invoices-grouped",
      dimension,
      filters,
      liquidationFilter,
      exceptionFilter,
      timePeriod,
      selectedCountries,
      selectedSalesPeople,
      queryAggregationFields,
    ],
    queryFn: async ({ signal }) => {
      const baseFilter = buildBaseFilter();
      const years = getYearNumbers();

      // Use monthly invoice query for month dimension
      if (dimension === "month") {
        // Determine max month for YTD
        const maxMonth = timePeriod === "ytd" ? getYtdMonth() : 12;

        // Check if there's an existing month filter from breadcrumbs
        const monthFilter = getMonthFilter();

        // Query all 3 years in parallel
        const yearResults = await Promise.all(
          (["CY", "PY", "PPY"] as YearType[]).map(async (yearType) => {
            const yearNum = years[yearType];
            const data = await fetchAllGroupedInvoices(
              (after) =>
                buildMonthlyInvoicesQuery(
                  yearNum,
                  baseFilter,
                  monthFilter,
                  queryAggregationFields,
                  { after },
                ),
              "Month",
              signal,
            );
            return { yearType, data };
          }),
        );

        // Process results - aggregate daily data by month
        const mergedData = new Map<
          number,
          {
            month: number;
            monthName: string;
            metrics: {
              [K in YearType]: {
                revenue: number;
                quantity: number;
                cost: number;
                profit: number;
              };
            };
          }
        >();

        yearResults.forEach(({ yearType, data }) => {
          data.forEach((group) => {
            // Extract month from Month field (1-12)
            const month = group.fields.Month as number;
            if (!month) return;

            const monthName = MONTH_NAMES[month - 1];

            if (!mergedData.has(month)) {
              mergedData.set(month, {
                month,
                monthName,
                metrics: {
                  CY: { revenue: 0, quantity: 0, cost: 0, profit: 0 },
                  PY: { revenue: 0, quantity: 0, cost: 0, profit: 0 },
                  PPY: { revenue: 0, quantity: 0, cost: 0, profit: 0 },
                },
              });
            }

            const entry = mergedData.get(month)!;
            // Sum up daily aggregations into monthly totals
            entry.metrics[yearType].revenue += group.aggregations?.revenue || 0;
            entry.metrics[yearType].quantity +=
              group.aggregations?.quantity || 0;
            entry.metrics[yearType].cost += group.aggregations?.cost || 0;
            entry.metrics[yearType].profit += group.aggregations?.profit || 0;
          });
        });

        // Convert to GridRow format (filter by YTD if needed)
        const rows: GridRow[] = [];
        const ytdMaxMonth = timePeriod === "ytd" ? maxMonth : 12;

        mergedData.forEach((data, month) => {
          // Skip months beyond YTD limit
          if (month > ytdMaxMonth) return;

          const row: GridRow = {
            id: String(month),
            dimensionValue: String(month),
            dimensionLabel: data.monthName,
            metrics: {
              CY: {},
              PY: {},
              PPY: {},
            },
          };

          // Calculate all metrics for each year
          (["CY", "PY", "PPY"] as YearType[]).forEach((year) => {
            const yearData = data.metrics[year];
            const prevYear =
              year === "CY" ? "PY" : year === "PY" ? "PPY" : null;
            const prevData = prevYear ? data.metrics[prevYear] : null;

            row.metrics[year].revenue = yearData.revenue;
            row.metrics[year].quantity = yearData.quantity;
            row.metrics[year].cost = yearData.cost;
            row.metrics[year].profit = yearData.profit;
            row.metrics[year].margin =
              yearData.revenue !== 0
                ? (yearData.profit / yearData.revenue) * 100
                : 0;

            if (prevData) {
              row.metrics[year].revenueDiffEuro =
                yearData.revenue - prevData.revenue;
              row.metrics[year].revenueDiffPercent =
                prevData.revenue !== 0
                  ? ((yearData.revenue - prevData.revenue) / prevData.revenue) *
                    100
                  : 0;
              row.metrics[year].quantityDiffNumber =
                yearData.quantity - prevData.quantity;
              row.metrics[year].quantityDiffPercent =
                prevData.quantity !== 0
                  ? ((yearData.quantity - prevData.quantity) /
                      prevData.quantity) *
                    100
                  : 0;
              row.metrics[year].costDiffEuro = yearData.cost - prevData.cost;
              row.metrics[year].costDiffPercent =
                prevData.cost !== 0
                  ? ((yearData.cost - prevData.cost) / prevData.cost) * 100
                  : 0;
              row.metrics[year].profitDiffEuro =
                yearData.profit - prevData.profit;
              row.metrics[year].profitDiffPercent =
                prevData.profit !== 0
                  ? ((yearData.profit - prevData.profit) / prevData.profit) *
                    100
                  : 0;
              const prevMargin =
                prevData.revenue !== 0
                  ? (prevData.profit / prevData.revenue) * 100
                  : 0;
              const curMargin = row.metrics[year].margin || 0;
              row.metrics[year].marginDiffPercent = curMargin - prevMargin;
            }
          });

          rows.push(row);
        });

        // Calculate revenue % total
        const totalRevenue = {
          CY: rows.reduce((sum, r) => sum + (r.metrics.CY.revenue || 0), 0),
          PY: rows.reduce((sum, r) => sum + (r.metrics.PY.revenue || 0), 0),
          PPY: rows.reduce((sum, r) => sum + (r.metrics.PPY.revenue || 0), 0),
        };

        rows.forEach((row) => {
          (["CY", "PY", "PPY"] as YearType[]).forEach((year) => {
            row.metrics[year].revenuePercentTotal =
              totalRevenue[year] !== 0
                ? ((row.metrics[year].revenue || 0) / totalRevenue[year]) * 100
                : 0;
          });
        });

        // Sort by month number
        return rows.sort(
          (a, b) => parseInt(a.dimensionValue) - parseInt(b.dimensionValue),
        );
      }

      // For non-month dimensions, use the factInvoices groupBy approach with Year/Month filters
      const groupByField = getDimensionScalarField(dimension);
      const monthFilter = getMonthFilter();
      const ytdMaxMonth = timePeriod === "ytd" ? getYtdMonth() : null;

      // Build 3 parallel queries - one for each year
      const yearQueries: { year: YearType; filter: Record<string, unknown> }[] =
        [
          {
            year: "CY",
            filter: {
              ...baseFilter,
              ...buildYearFilter(years.CY, monthFilter, ytdMaxMonth),
            },
          },
          {
            year: "PY",
            filter: {
              ...baseFilter,
              ...buildYearFilter(years.PY, monthFilter, ytdMaxMonth),
            },
          },
          {
            year: "PPY",
            filter: {
              ...baseFilter,
              ...buildYearFilter(years.PPY, monthFilter, ytdMaxMonth),
            },
          },
        ];

      // Execute all 3 queries in parallel
      const results = await Promise.all(
        yearQueries.map(async ({ year, filter }) => {
          const data = await fetchAllGroupedInvoices(
            (after) =>
              buildGroupedInvoicesQuery(
                filter,
                groupByField,
                queryAggregationFields,
                { after },
              ),
            groupByField,
            signal,
          );
          return { year, data };
        }),
      );

      // Merge results from all years into GridRow format
      const mergedData = new Map<
        string,
        {
          label: string;
          metrics: {
            [K in YearType]: {
              revenue: number;
              quantity: number;
              cost: number;
              profit: number;
            };
          };
        }
      >();

      results.forEach(({ year, data }) => {
        data.forEach((group) => {
          const fieldValue = group.fields[groupByField] as string | null;
          const key = getDimensionKey(dimension, fieldValue);
          const label = formatDimensionLabel(dimension, fieldValue);

          if (!mergedData.has(key)) {
            mergedData.set(key, {
              label,
              metrics: {
                CY: { revenue: 0, quantity: 0, cost: 0, profit: 0 },
                PY: { revenue: 0, quantity: 0, cost: 0, profit: 0 },
                PPY: { revenue: 0, quantity: 0, cost: 0, profit: 0 },
              },
            });
          }

          const entry = mergedData.get(key)!;
          // Sum up all values for the same key (e.g., all days in a month)
          entry.metrics[year].revenue += group.aggregations?.revenue || 0;
          entry.metrics[year].quantity += group.aggregations?.quantity || 0;
          entry.metrics[year].cost += group.aggregations?.cost || 0;
          entry.metrics[year].profit += group.aggregations?.profit || 0;
        });
      });

      // Convert to GridRow format with calculated metrics
      const rows: GridRow[] = [];

      mergedData.forEach((data, key) => {
        const row: GridRow = {
          id: key,
          dimensionValue: key,
          dimensionLabel: data.label,
          metrics: {
            CY: {},
            PY: {},
            PPY: {},
          },
        };

        // Calculate all metrics for each year
        (["CY", "PY", "PPY"] as YearType[]).forEach((year) => {
          const yearData = data.metrics[year];
          const prevYear = year === "CY" ? "PY" : year === "PY" ? "PPY" : null;
          const prevData = prevYear ? data.metrics[prevYear] : null;

          row.metrics[year].revenue = yearData.revenue;
          row.metrics[year].quantity = yearData.quantity;
          row.metrics[year].cost = yearData.cost;
          row.metrics[year].profit = yearData.profit;
          row.metrics[year].margin =
            yearData.revenue !== 0
              ? (yearData.profit / yearData.revenue) * 100
              : 0;

          if (prevData) {
            row.metrics[year].revenueDiffEuro =
              yearData.revenue - prevData.revenue;
            row.metrics[year].revenueDiffPercent =
              prevData.revenue !== 0
                ? ((yearData.revenue - prevData.revenue) / prevData.revenue) *
                  100
                : 0;
            row.metrics[year].quantityDiffNumber =
              yearData.quantity - prevData.quantity;
            row.metrics[year].quantityDiffPercent =
              prevData.quantity !== 0
                ? ((yearData.quantity - prevData.quantity) /
                    prevData.quantity) *
                  100
                : 0;
            row.metrics[year].costDiffEuro = yearData.cost - prevData.cost;
            row.metrics[year].costDiffPercent =
              prevData.cost !== 0
                ? ((yearData.cost - prevData.cost) / prevData.cost) * 100
                : 0;
            row.metrics[year].profitDiffEuro =
              yearData.profit - prevData.profit;
            row.metrics[year].profitDiffPercent =
              prevData.profit !== 0
                ? ((yearData.profit - prevData.profit) / prevData.profit) * 100
                : 0;
            const prevMargin =
              prevData.revenue !== 0
                ? (prevData.profit / prevData.revenue) * 100
                : 0;
            const curMargin = row.metrics[year].margin || 0;
            row.metrics[year].marginDiffPercent = curMargin - prevMargin;
          }
        });

        rows.push(row);
      });

      // Calculate revenue % total
      const totalRevenue = {
        CY: rows.reduce((sum, r) => sum + (r.metrics.CY.revenue || 0), 0),
        PY: rows.reduce((sum, r) => sum + (r.metrics.PY.revenue || 0), 0),
        PPY: rows.reduce((sum, r) => sum + (r.metrics.PPY.revenue || 0), 0),
      };

      rows.forEach((row) => {
        (["CY", "PY", "PPY"] as YearType[]).forEach((year) => {
          row.metrics[year].revenuePercentTotal =
            totalRevenue[year] !== 0
              ? ((row.metrics[year].revenue || 0) / totalRevenue[year]) * 100
              : 0;
        });
      });

      return rows;
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  // Apply sorting
  const sortedData = useMemo(() => {
    if (!data || !sortConfig) return data || [];

    return [...data].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      if (sortConfig.column === "dimension") {
        aVal = a.dimensionLabel;
        bVal = b.dimensionLabel;
      } else {
        const [metric, year] = sortConfig.column.split("-") as [
          MetricType,
          YearType,
        ];
        aVal = a.metrics[year]?.[metric] ?? 0;
        bVal = b.metrics[year]?.[metric] ?? 0;
      }

      if (typeof aVal === "string") {
        return sortConfig.direction === "asc"
          ? aVal.localeCompare(bVal as string)
          : (bVal as string).localeCompare(aVal);
      }

      return sortConfig.direction === "asc"
        ? aVal - (bVal as number)
        : (bVal as number) - aVal;
    });
  }, [data, sortConfig]);

  return {
    data: sortedData,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}
